import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/SiteFooter";
import { STATES, STATE_RESOURCES } from "@/lib/states";

const LAST_UPDATED = "30 June 2026";

/**
 * Legal Disclaimer, a free-standing page (separate from Terms and Privacy)
 * that consolidates every "this is not legal advice / not a substitute for
 * a solicitor" line we surface across the product. Linked from the global
 * SiteFooter on every page.
 *
 * Keep this page conservative in tone: it's the safety net behind every
 * piece of AI-generated content TenantTrail outputs.
 */
export default function Disclaimer() {
  return (
    <div className="min-h-screen bg-[#FAF7F0]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-12 sm:py-16" data-testid="disclaimer-page">
        <div className="label-mono mb-3">Legal</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#0D2B52] mb-3">Legal disclaimer</h1>
        <p className="text-sm text-[#6B7280] mb-10">Last updated: {LAST_UPDATED}</p>

        <div className="prose prose-sm max-w-none font-serif text-[15px] text-[#1A1A1A] leading-[1.75] space-y-6">

          <section>
            <h2 className="font-serif text-2xl text-[#0D2B52] mb-2">TenantTrail is not a law firm.</h2>
            <p>
              TenantTrail (operated by the company behind tenanttrail.net) is a software product that helps Australian renters organise their tenancy records and understand publicly-available information about Australian residential tenancy law. <strong>We are not a law firm. We do not provide legal advice. No solicitor-client relationship is created by your use of this site, our Compass AI assistant, or any document we help you produce.</strong>
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#0D2B52] mb-2">General information only</h2>
            <p>
              Everything you see in TenantTrail, including answers given by the Compass AI assistant, the Tenant Brain Q&amp;A library, our letter templates, evidence bundles, AI document summaries and any feedback on photographs or inspection reports, is{" "}
              <strong>general legal information</strong>, not legal advice. It is intended to help you make sense of your situation and prepare yourself, not to be relied upon as a substitute for professional advice.
            </p>
            <p>
              The law changes. State and territory legislation differs. Your individual circumstances may not match the situations Compass has been trained on. <strong>Before taking any significant action</strong>, especially anything involving court, tribunal, eviction, or money, you should consult a qualified solicitor or your free state Tenants&rsquo; Union service.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#0D2B52] mb-2">AI-generated content</h2>
            <p>
              Compass uses third-party large-language-model technology to generate responses, drafts and summaries. While we make every reasonable effort to keep Compass accurate, current and grounded in Australian tenancy legislation, <strong>large language models can make mistakes</strong>, miss recent legislative changes, or generate text that sounds confident but is incorrect. You must independently verify anything Compass tells you before relying on it.
            </p>
            <p>
              We do not warrant that any AI-generated content is accurate, complete, up-to-date, fit for a particular purpose, or free of errors. You use AI-generated content at your own risk.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#0D2B52] mb-2">No outcome guaranteed</h2>
            <p>
              Tribunal outcomes and dispute resolutions depend on the specific facts, the quality of evidence, the discretion of the decision-maker and many other factors outside our control. TenantTrail does not guarantee any particular outcome from using our tools, sending our letters, or submitting evidence bundles we help you generate.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#0D2B52] mb-2">Forensic claims</h2>
            <p>
              Our &ldquo;tamper-evident&rdquo; features (SHA-256 file fingerprints, EXIF preservation, timestamped chronologies, BCC mail-capture) are designed to make evidence harder to dispute. They are not a substitute for evidence rules at your tribunal or court. Tribunals and courts apply their own evidentiary standards and may accept, reject, or weight evidence at their discretion.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#0D2B52] mb-2">External links and third-party data</h2>
            <p>
              TenantTrail links to and surfaces public data from third-party sources, including state government websites, the NSW Fair Trading Name &amp; Shame register and tenant advocacy services. We do not control these third parties and are not responsible for the accuracy, currency or completeness of their information. Always verify against the original source before acting.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#0D2B52] mb-2">When to get proper legal advice</h2>
            <p>
              You should contact a qualified solicitor or your state&rsquo;s free Tenants&rsquo; Union service if you are facing any of the following:
            </p>
            <ul className="list-disc pl-6 space-y-1 my-3">
              <li>An eviction notice, termination notice or breach of agreement</li>
              <li>A tribunal hearing date or court summons</li>
              <li>A dispute involving more than a few hundred dollars</li>
              <li>Allegations of property damage, unpaid rent, or harassment</li>
              <li>Domestic violence concerns affecting your tenancy</li>
              <li>Discrimination or other complaints involving human rights legislation</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#0D2B52] mb-2">Free tenant services in your state</h2>
            <p>The following are the free, government-funded or government-recognised services for renters in each Australian state and territory:</p>
            <div className="not-prose grid sm:grid-cols-2 gap-x-6 gap-y-4 my-5 text-[13.5px]" data-testid="disclaimer-state-grid">
              {STATES.map((s) => (
                <div key={s.code} data-testid={`disclaimer-state-${s.code}`}>
                  <div className="font-serif text-[#0D2B52] mb-1">{s.name} <span className="font-mono text-[10px] text-[#6B7280]">({s.code})</span></div>
                  <ul className="space-y-0.5 text-[#5a6b7a]">
                    {(STATE_RESOURCES[s.code] || []).map(([name, url]) => (
                      <li key={url}>
                        <a href={url} target="_blank" rel="noopener noreferrer" className="hover:text-[#1B6C62] hover:underline">
                          {name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#0D2B52] mb-2">Australian law</h2>
            <p>
              TenantTrail operates from Australia and is governed by the laws of Australia. We comply with the <em>Privacy Act 1988 (Cth)</em>, the Australian Consumer Law (Schedule 2 of the <em>Competition and Consumer Act 2010 (Cth)</em>) and the residential tenancies legislation of the relevant state or territory where applicable.
            </p>
          </section>

          <section data-testid="disclaimer-security-section">
            <h2 className="font-serif text-2xl text-[#0D2B52] mb-2">Security and data handling</h2>
            <p>
              We take reasonable steps to keep your documents and account information secure. In plain English:
            </p>
            <ul className="list-disc pl-6 space-y-1 my-3">
              <li>
                <strong>Encrypted in transit.</strong> All traffic between your browser and TenantTrail uses HTTPS (TLS). Nothing you upload travels in cleartext over the public internet.
              </li>
              <li>
                <strong>Namespaced storage.</strong> Uploaded files live in a private, per-user path in our managed object-storage layer. There are no public URLs. Every download is authenticated against your account before any bytes are served.
              </li>
              <li>
                <strong>Passwords are hashed.</strong> We store bcrypt hashes, not passwords. Even we cannot read your password.
              </li>
              <li>
                <strong>Delete means delete.</strong> When you delete a document, we immediately overwrite the file in storage so the original content is no longer recoverable, and we mark the record as deleted in our database. Deletion is a one-way action, we cannot restore a file you have deleted.
              </li>
              <li>
                <strong>Backups are encrypted.</strong> Full system backups are encrypted at rest using an AES-based key that is held separately from the database itself.
              </li>
              <li>
                <strong>No sale of your data.</strong> We do not sell, rent or trade your personal information or your documents to third parties. See our <Link to="/privacy" className="text-[#1B6C62] hover:underline">Privacy Policy</Link> for the full picture.
              </li>
            </ul>
            <p>
              No system is 100% secure and we do not warrant that TenantTrail is invulnerable to attack, unauthorised access or human error. You should not upload documents that you are not willing to have stored in a cloud service. If your matter involves highly sensitive material (for example: material subject to a suppression order, a family violence intervention order, or classified information), speak to a solicitor before uploading it here.
            </p>
            <p>
              If you believe your account has been accessed without your consent, or you spot a security issue with TenantTrail, please contact us immediately via <Link to="/support" className="text-[#1B6C62] hover:underline">the support page</Link>.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#0D2B52] mb-2">Limitation of liability</h2>
            <p>
              To the maximum extent permitted by law, TenantTrail and its operators are not liable for any loss, damage or cost arising from your reliance on information, AI-generated content, letters, evidence bundles, or other outputs produced by or via this service. This includes (but is not limited to) tribunal outcomes, financial loss, or distress.
            </p>
            <p>
              Nothing in this disclaimer is intended to exclude any non-excludable consumer guarantee under the Australian Consumer Law.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#0D2B52] mb-2">Questions about this disclaimer</h2>
            <p>
              See our <Link to="/terms" className="text-[#1B6C62] hover:underline">Terms of Use</Link> and <Link to="/privacy" className="text-[#1B6C62] hover:underline">Privacy Policy</Link> for related material, or contact us via <Link to="/support" className="text-[#1B6C62] hover:underline">the support page</Link>.
            </p>
          </section>

        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
